/**
 * Normalizes an untrusted post-auth redirect target to a safe same-origin path.
 * Accepts only same-origin relative paths; everything else falls back to /account.
 *
 * Rejects:
 *  - null / undefined / empty / whitespace
 *  - protocol-relative URLs (//)
 *  - backslashes (browsers can treat /\ as protocol-relative)
 *  - control characters (including null bytes)
 *  - any URL whose resolved origin differs from the dummy origin
 *    (catches https://, javascript:, data:, etc.)
 */
export function safeInternalPath(raw: string | null | undefined): string {
  const FALLBACK = '/account';

  if (!raw || typeof raw !== 'string') return FALLBACK;

  const value = raw.trim();
  if (!value) return FALLBACK;

  // Must start with exactly one '/', not '//' (protocol-relative).
  if (!value.startsWith('/') || value.startsWith('//')) return FALLBACK;

  // Reject backslashes — browsers can treat /\ as protocol-relative.
  if (/[\\]/.test(value)) return FALLBACK;

  // Reject control characters (including null bytes).
   
  if (/[\x00-\x1f]/.test(value)) return FALLBACK;

  try {
    // Resolve against a dummy origin; if the resolved origin changes, the
    // value contained a scheme or host — reject it.
    const u = new URL(value, 'https://internal.invalid');
    if (u.origin !== 'https://internal.invalid') return FALLBACK;
    return u.pathname + u.search + u.hash;
  } catch {
    return FALLBACK;
  }
}
